// SuperAdminsListPage — /super/admins.
//
// Cross-tenant table of every platform admin (rows from `tricivo_admins`).
// Search + status filters. Per-row actions: Suspend (confirm modal →
// useUpdateAdminStatus) and Reset Password (modal form →
// useResetAdminPassword). Self-targeting suspend is blocked client-side
// by comparing the currently logged-in super admin's email to the row
// email (see `isSelf` helper).

import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, KeyRound, ShieldCheck, UserX } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { Modal } from '../../components/ui/Modal/Modal';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { Input } from '../../components/form/Input';
import { Select } from '../../components/form/Select';
import {
  useAllAdmins,
  useUpdateAdminStatus,
  useResetAdminPassword,
  type AdminListItem,
} from './hooks/useSuper';
import { useSuperAdminStore } from '../../store/superAdminStore';
import styles from './SuperAdminsListPage.module.css';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

const MIN_PASSWORD_LENGTH = 12;

function statusBadgeClass(s: AdminListItem['status']): string {
  if (s === 'active') return styles.badgeGreen ?? '';
  if (s === 'suspended') return styles.badgeRed ?? '';
  return styles.badgeMuted ?? '';
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB');
}

// The super-admin store only carries { name, email } — there is no
// `username` field — so we identify "self" by comparing the logged-in
// admin's email to the row's email.
function isSelfRow(
  row: Pick<AdminListItem, 'email'>,
  currentEmail: string | null | undefined,
): boolean {
  if (!currentEmail) return false;
  return currentEmail.toLowerCase() === row.email.toLowerCase();
}

interface ResetPasswordFormState {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  errors: {
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  };
}

const EMPTY_FORM: ResetPasswordFormState = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
  errors: {},
};

export function SuperAdminsListPage(): JSX.Element {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const currentAdmin = useSuperAdminStore((s) => s.admin);

  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const status = params.get('status') ?? '';
  const search = params.get('search') ?? '';

  const filters = useMemo(
    () => ({
      page,
      limit: 25,
      ...(status && { status: status as 'active' | 'inactive' | 'suspended' }),
      ...(search && { search }),
    }),
    [page, status, search],
  );

  const list = useAllAdmins(filters);
  const updateStatus = useUpdateAdminStatus();
  const resetPassword = useResetAdminPassword();

  const [suspendTarget, setSuspendTarget] = useState<AdminListItem | null>(null);
  const [resetTarget, setResetTarget] = useState<AdminListItem | null>(null);
  const [resetForm, setResetForm] = useState<ResetPasswordFormState>(EMPTY_FORM);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: false });
  }

  function openReset(row: AdminListItem) {
    setResetForm(EMPTY_FORM);
    setResetTarget(row);
  }

  function closeReset() {
    setResetTarget(null);
    setResetForm(EMPTY_FORM);
  }

  async function submitReset() {
    if (!resetTarget) return;
    const targetIsSelf = isSelfRow(resetTarget, currentAdmin?.email);
    const errors: ResetPasswordFormState['errors'] = {};

    if (targetIsSelf && resetForm.currentPassword.length === 0) {
      errors.currentPassword = 'Current password is required.';
    }
    if (resetForm.newPassword.length < MIN_PASSWORD_LENGTH) {
      errors.newPassword = `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
    if (resetForm.confirmPassword !== resetForm.newPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }

    if (Object.keys(errors).length > 0) {
      setResetForm((prev) => ({ ...prev, errors }));
      return;
    }

    await resetPassword.mutateAsync({
      encId: resetTarget.enc_id,
      body: {
        new_password: resetForm.newPassword,
        ...(targetIsSelf && { current_password: resetForm.currentPassword }),
      },
    });
    closeReset();
  }

  const admins = list.data?.admins ?? [];
  const total = list.data?.pagination?.total ?? 0;
  const totalPages = list.data?.pagination?.totalPages ?? 1;

  const resetTargetIsSelf = resetTarget
    ? isSelfRow(resetTarget, currentAdmin?.email)
    : false;

  return (
    <div className={styles.page}>
      <PageHeader
        title="Admins"
        subtitle={`${total} platform admins — manage access`}
        actions={
          <div className={styles.headerActions}>
            <Button
              variant="ghost"
              icon={ArrowLeft}
              onClick={() => navigate('/super')}
            >
              Back
            </Button>
            <Button variant="primary" onClick={() => navigate('/super/admins/new')}>
              New Admin
            </Button>
          </div>
        }
      />

      <Card>
        <div className={styles.filters}>
          <Input
            label="Search"
            value={search}
            onChange={(e) => setParam('search', e.target.value)}
            placeholder="username or email"
          />
          <Select
            label="Status"
            value={status}
            onChange={(e) => setParam('status', e.target.value)}
            options={STATUS_OPTIONS}
          />
        </div>
      </Card>

      <Card flush>
        {list.isLoading ? (
          <div className={styles.empty}>
            <Spinner />
          </div>
        ) : admins.length === 0 ? (
          <div className={styles.empty}>No admins match the filters.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Super</th>
                  <th>Last Login</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => {
                  const self = isSelfRow(a, currentAdmin?.email);
                  const suspendDisabled = self || a.status === 'suspended';
                  return (
                    <tr key={a.enc_id}>
                      <td>
                        <span className={styles.usernameCell}>{a.username}</span>
                      </td>
                      <td>{a.full_name}</td>
                      <td className={styles.mono}>{a.email}</td>
                      <td>
                        <span
                          className={`${styles.badge} ${statusBadgeClass(a.status)}`}
                        >
                          {a.status}
                        </span>
                      </td>
                      <td>
                        {a.is_super_admin ? (
                          <span
                            className={styles.superCell}
                            aria-label="Super admin"
                          >
                            <ShieldCheck size={16} aria-hidden />
                          </span>
                        ) : null}
                      </td>
                      <td>{formatDate(a.last_login_at)}</td>
                      <td>{formatDate(a.created_at)}</td>
                      <td>
                        <div className={styles.rowActions}>
                          <Button
                            variant="danger"
                            size="sm"
                            icon={UserX}
                            onClick={() => setSuspendTarget(a)}
                            disabled={suspendDisabled}
                          >
                            Suspend
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            icon={KeyRound}
                            onClick={() => openReset(a)}
                          >
                            Reset Password
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {totalPages > 1 ? (
        <div className={styles.pagination}>
          <Button
            variant="ghost"
            size="sm"
            disabled={page === 1}
            onClick={() => setParam('page', String(page - 1))}
          >
            Previous
          </Button>
          <span>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setParam('page', String(page + 1))}
          >
            Next
          </Button>
        </div>
      ) : null}

      <ConfirmModal
        open={suspendTarget !== null}
        title="Suspend this admin?"
        confirmLabel="Yes, suspend"
        variant="danger"
        loading={updateStatus.isPending}
        onConfirm={async () => {
          if (!suspendTarget) return;
          await updateStatus.mutateAsync({
            encId: suspendTarget.enc_id,
            body: { status: 'suspended' },
          });
          setSuspendTarget(null);
        }}
        onCancel={() => setSuspendTarget(null)}
        message={
          <span className={styles.modalMessage}>
            <ShieldCheck size={16} aria-hidden />
            <span>
              This will immediately invalidate all sessions for{' '}
              <strong>{suspendTarget?.full_name ?? ''}</strong>.
            </span>
          </span>
        }
      />

      <Modal
        open={resetTarget !== null}
        onClose={closeReset}
        title={
          resetTarget
            ? `Reset password for ${resetTarget.username}`
            : 'Reset password'
        }
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={closeReset}
              disabled={resetPassword.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitReset}
              loading={resetPassword.isPending}
            >
              Save
            </Button>
          </>
        }
      >
        <div className={styles.resetForm}>
          {resetTargetIsSelf ? (
            <Input
              label="Current Password"
              variant="password"
              value={resetForm.currentPassword}
              onChange={(e) =>
                setResetForm((prev) => ({
                  ...prev,
                  currentPassword: e.target.value,
                  errors: { ...prev.errors, currentPassword: undefined },
                }))
              }
              error={
                resetForm.errors.currentPassword
                  ? { message: resetForm.errors.currentPassword }
                  : undefined
              }
            />
          ) : null}
          <Input
            label="New Password"
            variant="password"
            value={resetForm.newPassword}
            onChange={(e) =>
              setResetForm((prev) => ({
                ...prev,
                newPassword: e.target.value,
                errors: { ...prev.errors, newPassword: undefined },
              }))
            }
            helper="Minimum 12 characters."
            error={
              resetForm.errors.newPassword
                ? { message: resetForm.errors.newPassword }
                : undefined
            }
          />
          <Input
            label="Confirm New Password"
            variant="password"
            value={resetForm.confirmPassword}
            onChange={(e) =>
              setResetForm((prev) => ({
                ...prev,
                confirmPassword: e.target.value,
                errors: { ...prev.errors, confirmPassword: undefined },
              }))
            }
            error={
              resetForm.errors.confirmPassword
                ? { message: resetForm.errors.confirmPassword }
                : undefined
            }
          />
        </div>
      </Modal>
    </div>
  );
}
